import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, FileText, List, Building2, MoreHorizontal, X, Settings, Users, Shield, Trash2, CreditCard, Terminal, ClipboardList, LogOut, LifeBuoy, MessageSquarePlus } from 'lucide-react';
import axios from 'axios';

import { API_BASE as API } from '../config';

/** Quick haptic tick for tab taps. Silent no-op on browsers that lack the API
 *  (i.e. desktop) so we can call it from every onClick without guards. */
const tick = () => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(8); } catch { /* ignore */ }
  }
};

/** True while the on-screen keyboard is covering enough of the viewport that
 *  pinning a 64px bar at the bottom would be in the way. Uses visualViewport
 *  on supported browsers (iOS Safari ≥ 13, Chrome ≥ 61, Firefox ≥ 91); falls
 *  back to focusin/focusout for older Androids. */
function useKeyboardOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (vv) {
      const onResize = () => setOpen(window.innerHeight - vv.height > 150);
      vv.addEventListener('resize', onResize);
      onResize();
      return () => vv.removeEventListener('resize', onResize);
    }
    // Fallback: hide whenever an input/textarea has focus.
    const onFocusIn = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) {
        setOpen(true);
      }
    };
    const onFocusOut = () => setOpen(false);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);
  return open;
}

export default function BottomNav() {
  const { user, hasRole, logout, operatorContext } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [supportCount, setSupportCount] = useState(0);
  const keyboardOpen = useKeyboardOpen();
  const inContext = user?.is_platform_operator && operatorContext?.tenantId;

  // Swipe-to-dismiss state for drawers
  const dragRef = useRef({ startY: 0, currentY: 0, dragging: false, target: null });
  const [dragOffset, setDragOffset] = useState(0);

  const closeDrawers = useCallback(() => {
    setMoreOpen(false);
    setCompaniesOpen(false);
    setDragOffset(0);
  }, []);

  useEffect(() => {
    if (!user?.is_platform_operator) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/support/queue/count`, { withCredentials: true });
        setSupportCount(res.data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user?.is_platform_operator]);

  // Fetch companies for the picker
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/companies`, { withCredentials: true })
      .then(res => setCompanies(res.data || []))
      .catch(() => {});
  }, [user, operatorContext?.tenantId, operatorContext?.orgId]);

  if (!user) return null;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    ...((!user?.is_platform_operator || inContext) && hasRole('super_admin', 'admin', 'manager', 'member') ? [{ path: '/intake', icon: FileText, label: 'Intake' }] : []),
    ...((!user?.is_platform_operator || inContext) ? [{ path: '/all-entries', icon: List, label: 'Entries' }] : []),
    ...(user?.is_platform_operator && !inContext
      ? [{ path: '/support', icon: LifeBuoy, label: 'Support', badge: supportCount }]
      : inContext
        ? [{ path: '/companies', icon: Building2, label: 'Companies' }]
        : [{ path: '/companies', icon: Building2, label: 'Companies' }]
    ),
  ];

  // Compute pending support count to surface as a badge on the More tab when
  // Support is hidden inside the drawer (in-context operator view).
  const moreBadge = (inContext && supportCount > 0) ? supportCount : 0;

  const moreItems = user?.is_platform_operator ? [
    ...(inContext ? [{ path: '/support', icon: LifeBuoy, label: 'Support', badge: supportCount }] : []),
    ...(inContext && operatorContext?.accountType === 'provider' && (user?.role === 'super_admin' || user?.role === 'admin') ? [{ path: '/organizations', icon: Building2, label: 'Organizations' }] : []),
    ...((user?.role === 'super_admin' || user?.role === 'admin') ? [{ path: '/user-management', icon: Users, label: 'User Mgmt' }] : []),
    ...((user?.role === 'super_admin' || user?.role === 'admin') ? [{ path: '/audit-log', icon: Shield, label: 'Audit Log' }] : []),
    ...(user?.is_developer ? [{ path: '/dev-hub', icon: Terminal, label: 'Dev Hub' }] : []),
    { path: '/settings', icon: Settings, label: 'Settings' },
  ] : [
    ...(hasRole('super_admin', 'admin', 'manager') ? [{ path: '/admin', icon: ClipboardList, label: 'Company Mgmt' }] : []),
    ...(user?.account_type === 'provider' && hasRole('super_admin', 'admin') ? [{ path: '/organizations', icon: Building2, label: 'Organizations' }] : []),
    ...((user?.role === 'super_admin' || user?.role === 'admin') ? [{ path: '/user-management', icon: Users, label: 'User Mgmt' }] : []),
    ...((user?.role === 'super_admin' || user?.role === 'admin') ? [{ path: '/audit-log', icon: Shield, label: 'Audit Log' }] : []),
    ...((user?.role === 'super_admin' || user?.role === 'admin') ? [{ path: '/recycle-bin', icon: Trash2, label: 'Recycle Bin' }] : []),
    ...(hasRole('super_admin', 'admin') ? [{ path: '/settings', icon: Settings, label: 'Settings' }] : []),
    ...(hasRole('super_admin', 'admin') ? [{ path: '/settings?tab=billing', icon: CreditCard, label: 'Billing' }] : []),
    ...(user?.is_developer ? [{ path: '/dev-hub', icon: Terminal, label: 'Dev Hub' }] : []),
  ];

  /** Scroll-to-top when the user re-taps the currently-active tab. Mimics the
   *  iOS / Android system gesture so the bottom bar feels native. */
  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  const handleNav = (path) => {
    tick();
    if (path === '/companies') {
      // Re-tapping the active Companies tab while the drawer is open closes it
      // (toggle behaviour matches the More tab).
      setCompaniesOpen((v) => !v);
      setMoreOpen(false);
      return;
    }
    // Re-tap on the active tab scrolls to top instead of navigating.
    if (isActive(path) && location.pathname === path) {
      scrollToTop();
      setMoreOpen(false);
      setCompaniesOpen(false);
      return;
    }
    navigate(path);
    setMoreOpen(false);
    setCompaniesOpen(false);
  };

  const handleCompanySelect = (companyName) => {
    tick();
    setCompaniesOpen(false);
    navigate('/companies', { state: { selectedCompany: companyName } });
  };

  const handleLogout = () => {
    tick();
    setMoreOpen(false);
    logout();
    navigate('/login');
  };

  const handleFeedback = () => {
    tick();
    setMoreOpen(false);
    // FeedbackButton listens for this event and opens its modal. Decoupling
    // via window event keeps the components independent.
    window.dispatchEvent(new CustomEvent('open-feedback'));
  };

  // Drag-to-dismiss handlers (touch only).
  const onDrawerTouchStart = (e, which) => {
    const t = e.touches[0];
    dragRef.current = { startY: t.clientY, currentY: t.clientY, dragging: true, target: which };
  };
  const onDrawerTouchMove = (e) => {
    if (!dragRef.current.dragging) return;
    const t = e.touches[0];
    const delta = Math.max(0, t.clientY - dragRef.current.startY);
    dragRef.current.currentY = t.clientY;
    setDragOffset(delta);
  };
  const onDrawerTouchEnd = () => {
    if (!dragRef.current.dragging) return;
    const delta = dragRef.current.currentY - dragRef.current.startY;
    dragRef.current.dragging = false;
    // Dismiss if the user dragged > 80px down. Otherwise spring back.
    if (delta > 80) {
      if (dragRef.current.target === 'more') setMoreOpen(false);
      if (dragRef.current.target === 'companies') setCompaniesOpen(false);
    }
    setDragOffset(0);
  };

  const anyDrawerOpen = moreOpen || companiesOpen;

  // Hide the entire bar when the on-screen keyboard is up so it doesn't cover
  // form inputs. Drawers also collapse — they'd be unusable anyway.
  if (keyboardOpen && !anyDrawerOpen) {
    return null;
  }

  return (
    <>
      {/* Shared overlay for drawers */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${
          anyDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawers}
        data-testid="drawer-overlay"
      />

      {/* More drawer */}
      <div
        className={`md:hidden fixed bottom-16 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-[0_-4px_24px_rgba(0,0,0,0.12)] border-t border-gray-200 max-h-[60vh] overflow-y-auto transition-[transform,opacity] duration-250 ease-out ${
          moreOpen ? 'opacity-100' : 'translate-y-[120%] opacity-0 pointer-events-none'
        }`}
        style={moreOpen ? { transform: `translateY(${dragOffset}px)` } : undefined}
        data-testid="more-drawer"
        onTouchStart={(e) => onDrawerTouchStart(e, 'more')}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
      >
        {/* Drawer handle */}
        <div className="flex justify-center pt-2 pb-0">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">More</span>
          <button onClick={() => { tick(); setMoreOpen(false); }} className="p-1 rounded-full hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="py-1">
          {moreItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => { tick(); setMoreOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                isActive(item.path.split('?')[0])
                  ? 'text-teal-600 bg-teal-50 font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`more-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className="w-4.5 h-4.5" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
          <button
            onClick={handleFeedback}
            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 w-full transition-colors border-t border-gray-100"
            data-testid="more-feedback"
          >
            <MessageSquarePlus className="w-4.5 h-4.5 text-teal-600" />
            Send Feedback
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
            data-testid="more-logout"
          >
            <LogOut className="w-4.5 h-4.5" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Companies picker drawer */}
      <div
        className={`md:hidden fixed bottom-16 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-[0_-4px_24px_rgba(0,0,0,0.12)] border-t border-gray-200 max-h-[60vh] overflow-y-auto transition-[transform,opacity] duration-250 ease-out ${
          companiesOpen ? 'opacity-100' : 'translate-y-[120%] opacity-0 pointer-events-none'
        }`}
        style={companiesOpen ? { transform: `translateY(${dragOffset}px)` } : undefined}
        data-testid="companies-drawer"
        onTouchStart={(e) => onDrawerTouchStart(e, 'companies')}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
      >
        {/* Drawer handle */}
        <div className="flex justify-center pt-2 pb-0">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">Select Company</span>
          <button onClick={() => { tick(); setCompaniesOpen(false); }} className="p-1 rounded-full hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="py-1">
          {companies.length > 0 ? companies.map((c) => (
            <button
              key={c.name}
              onClick={() => handleCompanySelect(c.name)}
              className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 w-full text-left transition-colors"
              data-testid={`company-pick-${c.name}`}
            >
              <Building2 className="w-4 h-4 text-gray-400" />
              {c.name}
            </button>
          )) : (
            <div className="px-4 py-6 text-center text-sm text-gray-400">No companies available</div>
          )}
        </div>
      </div>

      {/* Bottom nav bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-1px_6px_rgba(0,0,0,0.06)]" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-16 px-1">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNav(item.path)}
                className={`group relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-all active:scale-90 ${
                  active ? 'text-teal-600' : 'text-gray-400'
                }`}
                data-testid={`bnav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {/* Native-style pill behind the icon when active */}
                <div className={`absolute top-2 h-7 rounded-full bg-teal-100/80 transition-all duration-200 ease-out ${
                  active ? 'w-12 opacity-100 scale-100' : 'w-12 opacity-0 scale-75'
                }`} />
                <div className="relative">
                  <item.icon className={`w-5 h-5 transition-transform duration-150 ${active ? 'stroke-[2.5]' : ''}`} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full" data-testid="bnav-support-badge">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span
                  className={`relative max-w-full px-0.5 truncate text-[10px] leading-tight ${active ? 'font-semibold' : 'font-medium'}`}
                  title={item.label}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => { tick(); setCompaniesOpen(false); setMoreOpen((v) => !v); }}
            className={`relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-all active:scale-90 ${
              moreOpen ? 'text-teal-600' : 'text-gray-400'
            }`}
            data-testid="bnav-more"
          >
            {/* Pill background */}
            <div className={`absolute top-2 h-7 rounded-full bg-teal-100/80 transition-all duration-200 ease-out ${
              moreOpen ? 'w-12 opacity-100 scale-100' : 'w-12 opacity-0 scale-75'
            }`} />
            <div className="relative">
              <MoreHorizontal className={`w-5 h-5 ${moreOpen ? 'stroke-[2.5]' : ''}`} />
              {moreBadge > 0 && !moreOpen && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full" data-testid="bnav-more-badge">
                  {moreBadge > 99 ? '99+' : moreBadge}
                </span>
              )}
            </div>
            <span className={`relative text-[10px] leading-tight ${moreOpen ? 'font-semibold' : 'font-medium'}`}>More</span>
          </button>
        </div>
        {/* Safe area padding for iOS */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </nav>
    </>
  );
}
